import React, { useMemo, useState } from 'react';
import { get, ref } from 'firebase/database';
import {
  getBytes,
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
  uploadString,
} from 'firebase/storage';
import { database, storage } from '../firebase';

type CostMap = Record<string, number>;
type ProjectNode = Record<string, { total_cost?: number }>;
type VectorizerCost = {
  credits: number;
  totalCost: number;
};
type ToastType = 'neutral' | 'success' | 'error';
type StorageArtifactFolder = 'JSONS' | 'IMGS';
type StorageStep = 'folder' | 'files' | 'json' | 'image';
type PromptScope = 'preview' | 'standard' | 'global';

type StorageFileItem = {
  name: string;
  fullPath: string;
  exists: boolean;
};

type PromptTemplate = {
  archivo?: string;
  outputs?: unknown;
};

type StorageEditorState = {
  blockId: string;
  blockName: string;
  folderId: string;
  promptScope: PromptScope | null;
  step: StorageStep;
  artifactFolder: StorageArtifactFolder | null;
  files: StorageFileItem[];
  selectedFile: StorageFileItem | null;
  jsonValues: Record<string, string>;
  previewJsonPath: string;
  previewJsonValues: Record<string, string>;
  imageUrl: string;
  selectedImageFile: File | null;
  status: string;
  statusType: ToastType;
  isLoading: boolean;
  isSaving: boolean;
};

const promptPathMap: Record<PromptScope, string> = {
  preview: 'PromptsPreview',
  standard: 'PromptsStandard',
  global: 'PromptsGlobal',
};

const reservedJsonFileNames = new Set(['cuestionario.json', 'preview.json', 'manual_overrides.json']);

const sumValues = (map: CostMap) => Object.values(map).reduce((total, value) => total + value, 0);

const extractTotalCost = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return 0;
  }

  const entry = value as Record<string, unknown>;
  const direct = entry.total_cost;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }

  const pricing = entry.pricing;
  if (pricing && typeof pricing === 'object') {
    const nested = (pricing as Record<string, unknown>).total_cost;
    if (typeof nested === 'number' && Number.isFinite(nested)) {
      return nested;
    }
  }

  return 0;
};

const mapNode = (node?: ProjectNode): CostMap => {
  if (!node) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(node as Record<string, unknown>).map(([key, item]) => [key, extractTotalCost(item)])
  );
};

const mapPreviewSections = (projectValue: Record<string, unknown>): Record<string, CostMap> => {
  const previewEntries = Object.entries(projectValue)
    .filter(([key]) => /^Preview_\d+$/i.test(key))
    .sort(([a], [b]) => {
      const aId = Number((a.match(/^Preview_(\d+)$/i) || [])[1] ?? 0);
      const bId = Number((b.match(/^Preview_(\d+)$/i) || [])[1] ?? 0);
      return aId - bId;
    });
  if (!previewEntries.length) {
    return {};
  }

  return Object.fromEntries(previewEntries.map(([key, value]) => [key, mapNode(value as ProjectNode)]));
};

const mapRegenerationProjectSections = (
  projectValue: Record<string, unknown>
): Record<string, CostMap> => {
  const regenerationEntries = Object.entries(projectValue).filter(([key]) =>
    /^Regeneration_.+/.test(key)
  );
  if (!regenerationEntries.length) {
    return {};
  }

  return Object.fromEntries(
    regenerationEntries.map(([key, value]) => [key, mapNode(value as ProjectNode)])
  );
};

const mapVectorizerCosts = (projectValue: Record<string, unknown>): Record<string, VectorizerCost> => {
  const vectorizerNode = projectValue.VectorizerAI;
  if (!vectorizerNode || typeof vectorizerNode !== 'object') {
    return {};
  }

  const costs: Record<string, VectorizerCost> = {};
  const visit = (node: unknown, path: string[]) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    const entry = node as Record<string, unknown>;
    if ('credits' in entry || 'total_cost' in entry) {
      const credits = Number(entry.credits);
      costs[path.join('/')] = {
        credits: Number.isFinite(credits) ? credits : 0,
        totalCost: extractTotalCost(entry),
      };
      return;
    }

    Object.entries(entry).forEach(([key, value]) => visit(value, [...path, key]));
  };

  visit(vectorizerNode, []);
  return costs;
};

const getProjectPromptScope = (projectValue: Record<string, unknown>): PromptScope | null => {
  const projectType = String(projectValue.type ?? '').trim().toLowerCase();
  if (projectType === 'global' || projectType === 'standard') {
    return projectType;
  }

  const typePrice = String(projectValue.type_price ?? '').trim();
  if (typePrice === 'Admin_Global') {
    return 'global';
  }
  if (typePrice === 'Admin_Standard') {
    return 'standard';
  }

  return null;
};

const mapJsonToStrings = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, String(item ?? '')])
  );
};

const readStorageJsonMap = async (path: string): Promise<Record<string, string>> => {
  const bytes = await getBytes(storageRef(storage, path));
  const text = new TextDecoder().decode(bytes);
  return mapJsonToStrings(JSON.parse(text));
};

const parsePromptTemplates = (value: Record<string, PromptTemplate> | null): PromptTemplate[] => {
  if (!value) {
    return [];
  }

  return Object.entries(value)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, item]) => ({
      archivo: item?.archivo || '',
      outputs: item?.outputs,
    }));
};

const loadRequiredJsonKeys = async (scope: PromptScope | null, fileName: string): Promise<string[]> => {
  if (!scope || reservedJsonFileNames.has(fileName.toLowerCase())) {
    return [];
  }

  const snapshot = await get(ref(database, promptPathMap[scope]));
  const templates = parsePromptTemplates(snapshot.val() as Record<string, PromptTemplate> | null);
  const template = templates.find((item) => item.archivo === fileName);
  if (!template || !Array.isArray(template.outputs)) {
    return [];
  }

  return Array.from(
    new Set(
      template.outputs
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0)
    )
  );
};

const loadPromptJsonFileNames = async (scope: PromptScope | null): Promise<string[]> => {
  if (!scope) {
    return [];
  }

  const snapshot = await get(ref(database, promptPathMap[scope]));
  const templates = parsePromptTemplates(snapshot.val() as Record<string, PromptTemplate> | null);
  return Array.from(
    new Set(
      templates
        .map((template) => template.archivo || '')
        .map((fileName) => fileName.trim())
        .filter((fileName) => fileName.toLowerCase().endsWith('.json'))
        .filter((fileName) => !reservedJsonFileNames.has(fileName.toLowerCase()))
    )
  );
};

const removeEmptyJsonValues = (jsonValues: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(jsonValues).filter(([, value]) => value.trim().length > 0));

const addMissingJsonKeys = (
  jsonValues: Record<string, string>,
  requiredKeys: string[]
): Record<string, string> => {
  const nextValues = { ...jsonValues };
  requiredKeys.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(nextValues, key)) {
      nextValues[key] = '';
    }
  });
  return nextValues;
};

function ProjectsSection() {
  const [projectId, setProjectId] = useState('');
  const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});
  const [storageEditor, setStorageEditor] = useState<StorageEditorState | null>(null);
  const [costs, setCosts] = useState<{
    previewSections: Record<string, CostMap>;
    full: CostMap;
    regenerationSections: Record<string, CostMap>;
    vectorizer: Record<string, VectorizerCost>;
    selectedPreview: number | null;
    projectPromptScope: PromptScope | null;
  } | null>(null);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const loadProject = async (id: string) => {
    if (!id.trim()) {
      setStatus('Ingresa un Project ID válido.');
      return;
    }
    setIsLoading(true);
    setStatus('');
    setExpandedTables({});
    setStorageEditor(null);
    try {
      const snapshot = await get(ref(database, `Projects/${id.trim()}`));
      const value = snapshot.val() as {
        Full?: ProjectNode;
        type?: unknown;
        type_price?: unknown;
        selected_preview?: unknown;
        [key: string]: unknown;
      } | null;

      if (!value) {
        setCosts(null);
        setStatus('No se encontró información para este proyecto.');
        return;
      }

      const rawSelectedPreview = Number(value.selected_preview);
      const selectedPreview =
        Number.isInteger(rawSelectedPreview) && rawSelectedPreview >= 1 ? rawSelectedPreview : null;

      setCosts({
        previewSections: mapPreviewSections(value),
        full: mapNode((value.Full ?? value.full) as ProjectNode | undefined),
        regenerationSections: mapRegenerationProjectSections(value),
        vectorizer: mapVectorizerCosts(value),
        selectedPreview,
        projectPromptScope: getProjectPromptScope(value),
      });
    } catch (error) {
      setStatus('Error al cargar el proyecto.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTable = (tableId: string) => {
    setExpandedTables((current) => ({
      ...current,
      [tableId]: !current[tableId],
    }));
  };

  const openStorageEditor = (
    blockId: string,
    blockName: string,
    folderId: string,
    promptScope: PromptScope | null
  ) => {
    setStorageEditor({
      blockId,
      blockName,
      folderId,
      promptScope,
      step: 'folder',
      artifactFolder: null,
      files: [],
      selectedFile: null,
      jsonValues: {},
      previewJsonPath: '',
      previewJsonValues: {},
      imageUrl: '',
      selectedImageFile: null,
      status: '',
      statusType: 'neutral',
      isLoading: false,
      isSaving: false,
    });
  };

  const closeStorageEditor = () => {
    setStorageEditor(null);
  };

  const goBackStorageEditor = () => {
    setStorageEditor((current) => {
      if (!current) {
        return current;
      }
      if (current.step === 'json' || current.step === 'image') {
        return {
          ...current,
          step: 'files',
          selectedFile: null,
          jsonValues: {},
          previewJsonPath: '',
          previewJsonValues: {},
          imageUrl: '',
          selectedImageFile: null,
          status: '',
          statusType: 'neutral',
        };
      }
      if (current.step === 'files') {
        return {
          ...current,
          step: 'folder',
          artifactFolder: null,
          files: [],
          selectedFile: null,
          status: '',
          statusType: 'neutral',
        };
      }
      return null;
    });
  };

  const loadStorageFiles = async (artifactFolder: StorageArtifactFolder) => {
    if (!storageEditor) {
      return;
    }

    setStorageEditor((current) =>
      current
        ? {
            ...current,
            artifactFolder,
            step: 'files',
            files: [],
            selectedFile: null,
            previewJsonPath: '',
            previewJsonValues: {},
            status: 'Cargando archivos...',
            statusType: 'neutral',
            isLoading: true,
          }
        : current
    );

    try {
      const result = await listAll(storageRef(storage, `${storageEditor.folderId}/${artifactFolder}`));
      const files = result.items
        .filter(
          (item) =>
            artifactFolder !== 'JSONS' ||
            (item.name.toLowerCase() !== 'manual_overrides.json' &&
              (storageEditor.promptScope !== 'preview' || item.name.toLowerCase() !== 'preview.json'))
        )
        .map((item) => ({
          name: item.name,
          fullPath: item.fullPath,
          exists: true,
        }));
      const storedFileNames = new Set(files.map((file) => file.name));
      const missingPromptJsonFiles =
        artifactFolder === 'JSONS'
          ? (await loadPromptJsonFileNames(storageEditor.promptScope))
              .filter((fileName) => !storedFileNames.has(fileName))
              .map((fileName) => ({
                name: fileName,
                fullPath: `${storageEditor.folderId}/JSONS/${fileName}`,
                exists: false,
              }))
          : [];
      const nextFiles = [...files, ...missingPromptJsonFiles];
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              files: nextFiles,
              status: nextFiles.length ? '' : 'No hay archivos en esta carpeta.',
              statusType: 'neutral',
              isLoading: false,
            }
          : current
      );
    } catch (error) {
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Error al cargar archivos de Storage.',
              statusType: 'error',
              isLoading: false,
            }
          : current
      );
    }
  };

  const openStorageFile = async (file: StorageFileItem) => {
    if (!storageEditor?.artifactFolder) {
      return;
    }

    setStorageEditor((current) =>
      current
        ? {
            ...current,
            selectedFile: file,
            status: 'Cargando archivo...',
            statusType: 'neutral',
            isLoading: true,
          }
        : current
    );

    try {
      if (storageEditor.artifactFolder === 'JSONS') {
        const requiredKeys = await loadRequiredJsonKeys(storageEditor.promptScope, file.name);
        const jsonValues = file.exists ? await readStorageJsonMap(file.fullPath) : {};
        const previewJsonPath =
          storageEditor.promptScope === 'preview' && file.name.toLowerCase() !== 'cuestionario.json'
            ? `${storageEditor.folderId}/JSONS/preview.json`
            : '';
        let previewJsonValues: Record<string, string> = {};

        if (previewJsonPath) {
          try {
            previewJsonValues = await readStorageJsonMap(previewJsonPath);
          } catch (error) {
            previewJsonValues = {};
          }
        }

        setStorageEditor((current) =>
          current
            ? {
                ...current,
                step: 'json',
                jsonValues: addMissingJsonKeys(jsonValues, requiredKeys),
                previewJsonPath,
                previewJsonValues,
                status: '',
                statusType: 'neutral',
                isLoading: false,
              }
            : current
        );
        return;
      }

      const imageUrl = await getDownloadURL(storageRef(storage, file.fullPath));
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              step: 'image',
              imageUrl,
              selectedImageFile: null,
              status: '',
              statusType: 'neutral',
              isLoading: false,
            }
          : current
      );
    } catch (error) {
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Error al cargar el archivo.',
              statusType: 'error',
              isLoading: false,
            }
          : current
      );
    }
  };

  const updateJsonValue = (key: string, value: string) => {
    setStorageEditor((current) =>
      current
        ? {
            ...current,
            jsonValues: {
              ...current.jsonValues,
              [key]: value,
            },
          }
        : current
    );
  };

  const saveJsonFile = async () => {
    if (!storageEditor?.selectedFile) {
      return;
    }

    setStorageEditor((current) =>
      current
        ? {
            ...current,
            status: 'Guardando JSON...',
            statusType: 'neutral',
            isSaving: true,
          }
        : current
    );

    try {
      await uploadString(
        storageRef(storage, storageEditor.selectedFile.fullPath),
        JSON.stringify(removeEmptyJsonValues(storageEditor.jsonValues), null, 2),
        'raw',
        { contentType: 'application/json' }
      );
      const previewJsonKeys = new Set(Object.keys(storageEditor.previewJsonValues));
      const previewJsonKeysToUpdate = storageEditor.previewJsonPath
        ? Object.keys(storageEditor.jsonValues)
        : Object.keys(storageEditor.jsonValues).filter((key) => previewJsonKeys.has(key));

      if (storageEditor.previewJsonPath && previewJsonKeysToUpdate.length) {
        const nextPreviewJsonValues = { ...storageEditor.previewJsonValues };
        previewJsonKeysToUpdate.forEach((key) => {
          const value = storageEditor.jsonValues[key];
          if (value.trim().length > 0) {
            nextPreviewJsonValues[key] = value;
            return;
          }
          delete nextPreviewJsonValues[key];
        });
        await uploadString(
          storageRef(storage, storageEditor.previewJsonPath),
          JSON.stringify(removeEmptyJsonValues(nextPreviewJsonValues), null, 2),
          'raw',
          { contentType: 'application/json' }
        );
        setStorageEditor((current) =>
          current
            ? {
                ...current,
                previewJsonValues: nextPreviewJsonValues,
              }
            : current
        );
      }

      setStorageEditor((current) =>
        current
          ? {
              ...current,
              selectedFile: current.selectedFile ? { ...current.selectedFile, exists: true } : current.selectedFile,
              files: current.files.map((file) =>
                file.fullPath === storageEditor.selectedFile?.fullPath ? { ...file, exists: true } : file
              ),
              status:
                storageEditor.previewJsonPath && previewJsonKeysToUpdate.length
                  ? 'JSON guardado. preview.json actualizado.'
                  : 'JSON guardado.',
              statusType: 'success',
              isSaving: false,
            }
          : current
      );
    } catch (error) {
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Error al guardar JSON.',
              statusType: 'error',
              isSaving: false,
            }
          : current
      );
    }
  };

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      return;
    }

    setStorageEditor((current) =>
      current
        ? {
            ...current,
            selectedImageFile: file,
            imageUrl: URL.createObjectURL(file),
            status: '',
            statusType: 'neutral',
          }
        : current
    );
  };

  const saveImageFile = async () => {
    if (!storageEditor?.selectedFile || !storageEditor.selectedImageFile) {
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Selecciona una imagen para guardar.',
              statusType: 'error',
            }
          : current
      );
      return;
    }

    setStorageEditor((current) =>
      current
        ? {
            ...current,
            status: 'Guardando imagen...',
            statusType: 'neutral',
            isSaving: true,
          }
        : current
    );

    try {
      await uploadBytes(storageRef(storage, storageEditor.selectedFile.fullPath), storageEditor.selectedImageFile, {
        contentType: storageEditor.selectedImageFile.type,
      });
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Imagen guardada.',
              statusType: 'success',
              isSaving: false,
            }
          : current
      );
    } catch (error) {
      setStorageEditor((current) =>
        current
          ? {
              ...current,
              status: 'Error al guardar imagen.',
              statusType: 'error',
              isSaving: false,
            }
          : current
      );
    }
  };

  const renderStorageEditor = (tableId: string) => {
    if (!storageEditor || storageEditor.blockId !== tableId) {
      return null;
    }

    return (
      <div className="storage-editor">
        <div className="storage-editor-header">
          <div>
            <h4>
              {storageEditor.step === 'json' || storageEditor.step === 'image'
                ? `Editando ${storageEditor.selectedFile?.name ?? ''} para ${storageEditor.blockName}`
                : `Editando ${storageEditor.blockName}`}
            </h4>
            <div className="hint">{storageEditor.folderId}</div>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={
              storageEditor.step === 'json' || storageEditor.step === 'image'
                ? goBackStorageEditor
                : closeStorageEditor
            }
          >
            {storageEditor.step === 'json' || storageEditor.step === 'image' ? 'Atras' : 'Cancelar'}
          </button>
        </div>

        {storageEditor.step === 'folder' ? (
          <div className="storage-editor-actions">
            <button className="secondary-button" type="button" onClick={() => loadStorageFiles('JSONS')}>
              JSONS
            </button>
            <button className="secondary-button" type="button" onClick={() => loadStorageFiles('IMGS')}>
              IMGS
            </button>
          </div>
        ) : null}

        {storageEditor.step === 'files' ? (
          <div className="storage-editor-list">
            <div className="storage-editor-actions">
              <button className="secondary-button" type="button" onClick={() => loadStorageFiles('JSONS')}>
                JSONS
              </button>
              <button className="secondary-button" type="button" onClick={() => loadStorageFiles('IMGS')}>
                IMGS
              </button>
            </div>
            {storageEditor.files.map((file) => (
              <button
                className={`storage-file-item ${file.exists ? '' : 'storage-file-item-missing'}`}
                type="button"
                key={file.fullPath}
                onClick={() => openStorageFile(file)}
                disabled={storageEditor.isLoading}
              >
                <span className="mono">{file.name}</span>
                {file.exists ? null : <span className="storage-file-missing-label">Falta</span>}
              </button>
            ))}
          </div>
        ) : null}

        {storageEditor.step === 'json' ? (
          <div className="storage-json-editor">
            <div className="table table-compact">
              <div className="table-row table-header table-two-columns">
                <div>key</div>
                <div>value</div>
              </div>
              {Object.entries(storageEditor.jsonValues).map(([key, value]) => (
                <div className="table-row table-two-columns" key={key}>
                  <div className="mono">{key}</div>
                  <input
                    className="table-input"
                    type="text"
                    value={value}
                    onChange={(event) => updateJsonValue(key, event.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="storage-editor-footer">
              <button className="primary-button" type="button" onClick={saveJsonFile} disabled={storageEditor.isSaving}>
                {storageEditor.isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : null}

        {storageEditor.step === 'image' ? (
          <div className="storage-image-editor">
            {storageEditor.imageUrl ? (
              <img className="storage-image-preview" src={storageEditor.imageUrl} alt={storageEditor.selectedFile?.name ?? ''} />
            ) : null}
            <label className="field">
              <span>Nueva imagen</span>
              <input type="file" accept="image/*" onChange={handleImageSelection} />
            </label>
            <div className="storage-editor-footer">
              <button className="primary-button" type="button" onClick={saveImageFile} disabled={storageEditor.isSaving}>
                {storageEditor.isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : null}

        {storageEditor.status ? (
          <div className={`inline-toast inline-toast-${storageEditor.statusType}`}>{storageEditor.status}</div>
        ) : null}
      </div>
    );
  };

  const renderProjectTable = (
    tableId: string,
    title: string,
    total: number,
    sectionCosts: CostMap,
    folderId?: string,
    promptScope?: PromptScope | null
  ) => {
    const isExpanded = Boolean(expandedTables[tableId]);

    return (
      <div className="project-table" key={tableId}>
        <div className="project-table-title-row">
          <button
            className="project-table-toggle"
            type="button"
            aria-expanded={isExpanded}
            onClick={() => toggleTable(tableId)}
          >
            <h3>{title}</h3>
            <span className={`project-table-chevron ${isExpanded ? 'project-table-chevron-open' : ''}`}>
              v
            </span>
          </button>
          <div className="project-table-summary">
            <span className="project-total">Total: {total.toFixed(6)}</span>
            {folderId ? (
              <button
                className="secondary-button project-storage-button"
                type="button"
                onClick={() => openStorageEditor(tableId, title, folderId, promptScope ?? null)}
              >
                Editar Storage
              </button>
            ) : null}
          </div>
        </div>
        {renderStorageEditor(tableId)}
        {isExpanded ? (
          <div className="table table-compact">
            <div className="table-row table-header table-two-columns">
              <div>Clave</div>
              <div>Costo</div>
            </div>
            {Object.entries(sectionCosts).length ? (
              Object.entries(sectionCosts).map(([key, value]) => (
                <div className="table-row table-two-columns" key={`${tableId}-${key}`}>
                  <div className="mono">{key}</div>
                  <div>{value.toFixed(6)}</div>
                </div>
              ))
            ) : (
              <div className="table-row table-two-columns">
                <div className="table-placeholder">-</div>
                <div className="table-placeholder">0</div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const handleSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await loadProject(projectId);
  };

  const renderVectorizerTable = (vectorizerCosts: Record<string, VectorizerCost>) => {
    const total = Object.values(vectorizerCosts).reduce((sum, item) => sum + item.totalCost, 0);
    const isExpanded = Boolean(expandedTables.vectorizer);

    return (
      <div className="project-table">
        <div className="project-table-title-row">
          <button
            className="project-table-toggle"
            type="button"
            aria-expanded={isExpanded}
            onClick={() => toggleTable('vectorizer')}
          >
            <h3>COSTO VECTORIZER AI</h3>
            <span className={`project-table-chevron ${isExpanded ? 'project-table-chevron-open' : ''}`}>v</span>
          </button>
          <div className="project-table-summary">
            <span className="project-total">Total: USD {total.toFixed(6)}</span>
          </div>
        </div>
        {isExpanded ? (
          <div className="table table-compact">
            <div className="table-row table-header vectorizer-cost-row">
              <div>Logo</div>
              <div>Créditos</div>
              <div>Costo USD</div>
            </div>
            {Object.entries(vectorizerCosts).length ? (
              Object.entries(vectorizerCosts).map(([key, value]) => (
                <div className="table-row vectorizer-cost-row" key={`vectorizer-${key}`}>
                  <div className="mono">{key}</div>
                  <div>{value.credits}</div>
                  <div>USD {value.totalCost.toFixed(6)}</div>
                </div>
              ))
            ) : (
              <div className="table-row vectorizer-cost-row">
                <div className="table-placeholder">-</div>
                <div className="table-placeholder">0</div>
                <div className="table-placeholder">USD 0</div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const summary = useMemo(() => {
    const previewTotal = Object.values(costs?.previewSections ?? {}).reduce(
      (total, sectionCostMap) => total + sumValues(sectionCostMap),
      0
    );
    const fullTotal = sumValues(costs?.full ?? {});
    const regenerationTotal = Object.values(costs?.regenerationSections ?? {}).reduce(
      (total, sectionCostMap) => total + sumValues(sectionCostMap),
      0
    );
    const vectorizerTotal = Object.values(costs?.vectorizer ?? {}).reduce(
      (total, item) => total + item.totalCost,
      0
    );

    return {
      previewTotal,
      fullTotal,
      regenerationTotal,
      vectorizerTotal,
      mainTotal: previewTotal + fullTotal + regenerationTotal + vectorizerTotal,
    };
  }, [costs]);

  return (
    <div className="section">
      <div className="section-header">
        <h2>Detalle de proyectos</h2>
      </div>
      <form className="project-search" onSubmit={handleSearch}>
        <label className="field">
          <span>Project ID</span>
          <input
            type="text"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            placeholder="ID del proyecto"
          />
        </label>
        <button className="primary-button" type="submit" disabled={isLoading}>
          {isLoading ? 'Buscando...' : 'Buscar'}
        </button>
        {status ? <div className="inline-toast inline-toast-neutral">{status}</div> : null}
      </form>

      {costs ? (
        <div className="project-tables">
          <div className="project-main-total">
            <span>Costo total</span>
            <strong>{summary.mainTotal.toFixed(6)}</strong>
          </div>
          {costs.selectedPreview !== null ? (
            <div className="hint">Preview seleccionado: {costs.selectedPreview}</div>
          ) : null}
          {renderVectorizerTable(costs.vectorizer)}
          {Object.entries(costs.previewSections).length ? (
            Object.entries(costs.previewSections).map(([sectionName, sectionCosts]) => (
              renderProjectTable(
                `preview-${sectionName}`,
                sectionName,
                sumValues(sectionCosts),
                sectionCosts,
                `${projectId.trim()}/${sectionName}`,
                'preview'
              )
            ))
          ) : (
            renderProjectTable('preview-empty', 'Preview', 0, {})
          )}
          {renderProjectTable(
            'full',
            'Full',
            summary.fullTotal,
            costs.full,
            projectId.trim(),
            costs.projectPromptScope
          )}
          {Object.entries(costs.regenerationSections).length ? (
            Object.entries(costs.regenerationSections).map(([sectionName, sectionCosts]) => (
              renderProjectTable(
                `regeneration-${sectionName}`,
                sectionName,
                sumValues(sectionCosts),
                sectionCosts,
                `${projectId.trim()}/${sectionName}`,
                costs.projectPromptScope
              )
            ))
          ) : (
            renderProjectTable('regeneration-empty', 'Regeneration', 0, {})
          )}
        </div>
      ) : null}
    </div>
  );
}

export default ProjectsSection;
